import actionCreatorFactory from "../util/redux/action-factory";
import { reducerWithInitialState } from "../util/redux/typed-reducers";

import * as request from "../util/request";
import { env } from "../util/env";
import { ReduxState, ReduxDispatch } from "./reducers";
import {
    LoanDto,
    LoanNameEditDto,
    LoanEditInfoDto,
    LoanEditRequestDto,
    LoanChangeRequestTypeDto,
} from "../util/DTOs/LoanDtos";
import { showSnackbar } from "./snackbar";
import { formValueSelector } from "redux-form";
import logger from "../util/logger";
import { formatNumber } from "util/utils";

export interface Loans {
    [id: number]: LoanDto;
}

/**
 * Action factory + actions
 */
const actionFactory = actionCreatorFactory("HypoMobiili/Loan");
const getLoanAction = actionFactory<number, LoanDto, string>("GET_LOAN");

const getAllLoansAction = actionFactory<undefined, Loans, string>(
    "GET_ALL_LOANS"
);

const getLoanChangeRequestTypesAction = actionFactory<
    {},
    LoanChangeRequestTypeDto[],
    string
>("GET_LOAN_CHANGE_REQUEST_TYPES");

const editLoanNameAction = actionFactory<{}, LoanNameEditDto, string>(
    "EDIT_LOAN_NAME"
);

const saveLoanEditFormAction = actionFactory<{}, LoanEditRequestDto, string>(
    "SEND_EDIT_LOAN"
);

export function getLoanChangeRequestTypes() {
    return async (dispatch: ReduxDispatch) => {
        try {
            dispatch(getLoanChangeRequestTypesAction({}));

            const result = await request.get<Array<LoanChangeRequestTypeDto>>(
                `${env.APP_API_HOST}/api/types/loanChangeRequest`
            );

            dispatch(getLoanChangeRequestTypesAction.done(result));
            return result;
        } catch (error) {
            dispatch(
                getLoanChangeRequestTypesAction.failed(
                    "Error loading request types"
                )
            );
            throw error;
        }
    };
}

export function getLoan(id: number) {
    return async (dispatch: ReduxDispatch) => {
        try {
            dispatch(getLoanAction(id));

            const result = await request.get<LoanDto>(
                `${env.APP_API_HOST}/api/loans/${id}`
            );

            dispatch(getLoanAction.done(result));
            return result;
        } catch (error) {
            dispatch(getLoanAction.failed("Error loading loan data"));
            throw error;
        }
    };
}

export function getAllLoans() {
    return async (dispatch: ReduxDispatch) => {
        try {
            dispatch(getAllLoansAction(undefined));

            const result = await request.get<Array<LoanDto>>(
                `${env.APP_API_HOST}/api/loans`
            );

            let loans: Loans = {};
            result.forEach((loan) => {
                loans[loan.id] = loan;
            });
            dispatch(getAllLoansAction.done(loans));
        } catch (error) {
            dispatch(getAllLoansAction.failed("Error loading all loans data"));
            throw error;
        }
    };
}

export function editLoanName(data: LoanNameEditDto) {
    return async (dispatch: ReduxDispatch) => {
        try {
            dispatch(editLoanNameAction({}));

            const result = await request.post<LoanNameEditDto>(
                `${env.APP_API_HOST}/api/loans/${data.id}`,
                data
            );

            dispatch(editLoanNameAction.done(result));
        } catch (error) {
            logger.error(error);
            dispatch(editLoanNameAction.failed(""));
            dispatch(showSnackbar("Nimen vaihtaminen epäonnistui"));
        }
    };
}

export function saveLoanEditForm(data: LoanEditRequestDto) {
    return async (dispatch: ReduxDispatch) => {
        try {
            dispatch(saveLoanEditFormAction({}));

            const result = await request.post<LoanEditRequestDto>(
                `${env.APP_API_HOST}/api/loans/changeRequest`,
                data
            );

            dispatch(saveLoanEditFormAction.done(result));
        } catch (error) {
            logger.error(error);
            dispatch(saveLoanEditFormAction.failed(""));
            dispatch(showSnackbar("Muutospyynnön lähetys epäonnistui"));
            throw error;
        }
    };
}

/**
 * The internal state of this reducer
 */
export class LoanState {
    loans: Loans;
    loading: boolean;
    loanEditInfo: Array<LoanEditInfoDto>;
    lastLoanEditRequest: LoanEditRequestDto;
}

/**
 * The reducer.
 *
 * The module exports this as default
 */

const initialLoanState: LoanState = {
    loans: {},
    loading: false,
    loanEditInfo: [],
    lastLoanEditRequest: {
        loanId: 0,
        loanNumber: "",
        editTypeId: 0,
        maturityDate: 0,
        monthlyAmount: 0,
        loanEndDate: new Date(),
        startDate: new Date(),
        endDate: new Date(),
        contactByEmail: false,
        contactByPhone: false,
        reasonAndDetails: "",
    },
};

export const LoanReducer = reducerWithInitialState(initialLoanState)
    .case(getLoanAction.done, (state, action) => {
        return {
            ...state,
            loans: {
                ...state.loans,
                [action.payload.id]: action.payload,
            },
        };
    })
    .case(getAllLoansAction.done, (state, action) => {
        return {
            ...state,
            loans: action.payload,
        };
    })
    .case(editLoanNameAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(editLoanNameAction.done, (state, action) => {
        const id = action.payload.id;

        return {
            ...state,
            loans: {
                ...state.loans,
                [id]: {
                    ...state.loans[id],
                    loanName: action.payload.loanName,
                },
            },
            loading: false,
        };
    })
    .case(editLoanNameAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(saveLoanEditFormAction.done, (state, action) => {
        return {
            ...state,
            lastLoanEditRequest: action.payload,
        };
    })
    .case(saveLoanEditFormAction.failed, (state, action) => {
        return {
            ...state,
        };
    })
    .case(getLoanChangeRequestTypesAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(getLoanChangeRequestTypesAction.done, (state, action) => {
        const editInfos = action.payload.map((item) => {
            return {
                editTypeId: item.id,
                name: item.name,
                price:
                    item.price !== null
                        ? `${formatNumber(item.price, 2)} €`
                        : item.priceDescription,
            } as LoanEditInfoDto;
        });

        return {
            ...state,
            loading: false,
            loanEditInfo: editInfos,
        };
    })
    .case(getLoanChangeRequestTypesAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    });

export default LoanReducer;

/**
 * Selectors
 */
export function getLoanData(state: ReduxState, id: number) {
    return (
        (state && state.loan && state.loan.loans && state.loan.loans[id]) ||
        false
    );
}

export function getAllLoansData(state: ReduxState) {
    return state.loan.loans;
}

export function getAllLoansArray(state: ReduxState) {
    let loanArray: LoanDto[] = [];

    Object.keys(state.loan.loans).map((key) => {
        loanArray.push(state.loan.loans[key]);
    });

    return loanArray;
}

export function getEditTypeId(state: ReduxState) {
    const selector = formValueSelector("loanEditForm");
    const editTypeId = selector(state, "editTypeId");
    return editTypeId;
}

export function hasMultipleDebtors(state: ReduxState) {
    let multipleDebtors = false;
    if (state.loan.loans[state.loan.lastLoanEditRequest.loanId]) {
        if (
            state.loan.loans[state.loan.lastLoanEditRequest.loanId].debtors
                .length > 1
        ) {
            multipleDebtors = true;
        }
    }
    return multipleDebtors;
}

export function getEditTypeIdList(state: ReduxState) {
    return state.loan.loanEditInfo;
}

/**
 * Helpers
 */
export const emptyLoanData: LoanDto = {
    id: 0,
    loanNumber: "",
    debtors: [],
    interestInfo: {
        referenceRate: "",
    },
    loanName: "",
    paymentPlan: [],
    instalments: [],
    loanTypeName: "Luotto",
};



// WEBPACK FOOTER //
// ./src/ducks/loan.ts